import {
    ProjectMemberRole,
    ProjectMemberRoleLabels,
} from '../types/projectMemberRole';
import type { RoleWithScopes } from '../types/roles';

/**
 * Utility functions to convert project member roles to equivalent scope sets
 * for testing migration compatibility between role-based and scope-based authorization
 */

/**
 * Base scopes for each role level (without inheritance)
 */
const BASE_ROLE_SCOPES = {
    [ProjectMemberRole.VIEWER]: [
        // Basic viewing permissions
        'view:Dashboard',
        'view:JobStatus@self', // For viewing job status created by user
        'view:SavedChart',
        'view:Space',
        'view:Project',
        'view:PinnedItems',
        'view:DashboardComments',
        'view:Tags',
        'manage:ExportCsv',
        // Sortment related view permissions
        'view:Relation',
        'view:VisualAudience',
        'view:DbAudience',
        'view:SQLAudience',
        'view:Kpi',
        'view:Campaign',
        'view:Catalog',
        'view:Collections',
        'view:ProjectAccessManagement',
        'view:CustomMetric',
        'view:TestProfiles',
        'view:ContactFields',
        'view:Journeys',
        'view:SyncSchedule',
        'view:AgentTask',
        'view:AgentChat@self',
        'manage:AgentChat@self',

        // Enterprise scopes (when available)
        'view:MetricsTree',
        'view:SpotlightTableConfig',
        'view:AiAgentThread@self',
    ],

    [ProjectMemberRole.INTERACTIVE_VIEWER]: [
        // Additional interactive viewer permissions
        'view:UnderlyingData',
        'view:SemanticViewer',
        'manage:Explore',
        'manage:ChangeCsvResults',
        'create:ScheduledDeliveries',
        'manage:ScheduledDeliveries@self',

        // Enterprise scopes
        'view:AiAgent',
        'create:AiAgentThread',
    ],

    [ProjectMemberRole.EDITOR]: [
        // Editor-specific permissions
        'create:Space',
        'create:DashboardComments',
        'manage:Space@public', // For non-private spaces
        'manage:Space@assigned', // Via space access (admin role)
        'manage:Job',
        'manage:PinnedItems',
        'manage:ScheduledDeliveries@self',
        'manage:GoogleSheets',
        'manage:DashboardComments',
        'manage:Tags',
        'manage:Dashboard@space', // Via space access
        'manage:SavedChart@space', // Via space access
        'manage:CustomMetric',
        'manage:Kpi',

        // Enterprise scopes
        'manage:MetricsTree',
        'manage:AiAgentThread@self', // User's own threads
    ],

    [ProjectMemberRole.DEVELOPER]: [
        // Developer-specific permissions
        'manage:VirtualView',
        'manage:CustomSql',
        'manage:SqlRunner',
        'manage:Validation',
        'manage:CompileProject',
        'create:Project@preview', // Preview projects
        'delete:Project@self', // Preview projects created by user
        'update:Project',
        'update:Project@self',
        'view:JobStatus', // All jobs in project
        'view:SourceCode',
        'manage:SourceCode',
        'promote:SavedChart@space',
        'promote:Dashboard@space',
        'manage:AgentTask',
        'manage:AgentChat',

        // Enterprise scopes
        'manage:SpotlightTableConfig',
        'manage:AiAgent',
        'manage:AiAgentThread@self', // User's own threads
    ],

    [ProjectMemberRole.ADMIN]: [
        'manage:Quest',
        // Admin-specific permissions
        'delete:Project', // Any project
        'view:Analytics',
        'manage:Dashboard', // All dashboards
        'manage:Space', // All spaces
        'manage:Project', // Required for managing non-private spaces
        'manage:SavedChart', // All saved charts
        'view:AiAgentThread', // All threads in project
        'manage:AiAgentThread', // All threads in project
        'manage:ScheduledDeliveries',
        // Sortment related admin permissions
        'manage:Relation',
        'manage:Cache',
        'manage:Hidden',
        'manage:Joins',
        'manage:Masked',
        'manage:Table',
        'manage:TableAttributes',
        'manage:TableSchema',
        'view:Cache',
        'view:Hidden',
        'view:Joins',
        'view:Masked',
        'view:Table',
        'view:TableAttributes',
        'view:TableSchema',
        'manage:DbAudience',
        'manage:VisualAudience',
        'manage:SQLAudience',
        'manage:Campaign',
        'manage:Catalog',
        'manage:Collections',
        'manage:EventsMapper',
        'manage:Integrations',
        'manage:Profile',
        'manage:SubscriptionCategory',
        'manage:SubscriptionGroup',
        'manage:SyncSchedule',
        'view:Integrations',
        'view:EventMappers',
        'create:EventMappers',
        'update:EventMappers',
        'delete:EventMappers',
        'create:TestProfiles',
        'update:TestProfiles',
        'create:ContactFields',
        'update:ContactFields',
        'create:Journeys',
        'update:Journeys',
        'delete:Journeys',
        'manage:Journeys',
        'view:ProjectSettings',
        'view:ProjectSettingsOverview',
        'view:ProjectSettingsDataConnection',
        'view:ProjectSettingsBlobStorage',
        'view:ProjectSettingsSyncSchedules',
        'view:ProjectSettingsChannels',
        'view:ProjectSettingsSubscriptionGroups',
        'view:ProjectSettingsDeliveryControls',
        'view:ProjectSettingsEventSources',
        'view:ProjectSettingsMembers',
        'view:ProjectSettingsReachabilityVariables',
        'manage:ProjectSettingsOverview',
        'manage:ProjectSettingsDataConnection',
        'manage:ProjectSettingsBlobStorage',
        'manage:ProjectSettingsSyncSchedules',
        'manage:ProjectSettingsChannels',
        'manage:ProjectSettingsSubscriptionGroups',
        'manage:ProjectSettingsDeliveryControls',
        'manage:ProjectSettingsMembers',
        'manage:ProjectSettingsReachabilityVariables',
        'create:ProjectSettingsEventSources',
        'update:ProjectSettingsEventSources',
        'delete:ProjectSettingsEventSources',
        'delete:ProjectSettingsSubscriptionGroups',
    ],
    [ProjectMemberRole.MARKETER]: ['manage:Quest'],
    [ProjectMemberRole.SUPPORT]: [
        'view:Project',
        'view:Quest',
        'view:AgentChat@self',
        'manage:AgentChat@self',
        'view:Profile',
        'view:Relation',
        'manage:SqlRunner',
        'view:ProjectSettingsSubscriptionGroups',
        'update:ProjectSettingsSubscriptionGroups',
    ],
} as const;

/**
 * Role hierarchy for inheritance
 */
const ROLE_HIERARCHY = [
    ProjectMemberRole.VIEWER,
    ProjectMemberRole.INTERACTIVE_VIEWER,
    ProjectMemberRole.EDITOR,
    ProjectMemberRole.DEVELOPER,
    ProjectMemberRole.ADMIN,
] as const;

/**
 * Maps project member roles to their equivalent scopes based on projectMemberAbility.ts analysis
 * Each role inherits permissions from the roles below it in the hierarchy
 */
export const PROJECT_ROLE_TO_SCOPES_MAP: Record<ProjectMemberRole, string[]> =
    (() => {
        const result = {} as Record<ProjectMemberRole, string[]>;

        for (const role of ROLE_HIERARCHY) {
            const roleIndex = ROLE_HIERARCHY.indexOf(role);
            const inheritedScopes = new Set<string>();

            // Add scopes from all lower-level roles
            for (let i = 0; i <= roleIndex; i += 1) {
                const currentRole = ROLE_HIERARCHY[i];
                BASE_ROLE_SCOPES[currentRole].forEach((scope) =>
                    inheritedScopes.add(scope),
                );
            }

            result[role] = Array.from(inheritedScopes);
        }

        result[ProjectMemberRole.VIEWER] = [
            ...result[ProjectMemberRole.VIEWER],
            'view:Quest',
        ];

        result[ProjectMemberRole.MARKETER] = Array.from(
            new Set([
                ...result[ProjectMemberRole.DEVELOPER],
                ...BASE_ROLE_SCOPES[ProjectMemberRole.MARKETER],
            ]),
        );
        result[ProjectMemberRole.SUPPORT] = [
            ...BASE_ROLE_SCOPES[ProjectMemberRole.SUPPORT],
        ];

        return result;
    })();

/**
 * Gets the scopes required for a specific project member role
 */
export const getAllScopesForRole = (role: ProjectMemberRole): string[] => [
    ...PROJECT_ROLE_TO_SCOPES_MAP[role],
];

/**
 * Gets only the non-enterprise scopes for a role (filters out enterprise-only features)
 */
export const getNonEnterpriseScopesForRole = (
    role: ProjectMemberRole,
): string[] => {
    const enterpriseScopes = new Set([
        'view:MetricsTree',
        'manage:MetricsTree',
        'view:SpotlightTableConfig',
        'manage:SpotlightTableConfig',
        'view:AiAgent',
        'view:AiAgentThread',
        'view:AiAgentThread@self',
        'create:AiAgentThread',
        'manage:AiAgent',
        'manage:AiAgentThread',
        'manage:AiAgentThread@self',
        'manage:ContentAsCode',
        'manage:PersonalAccessToken',
    ]);

    return PROJECT_ROLE_TO_SCOPES_MAP[role].filter(
        (scope) => !enterpriseScopes.has(scope),
    );
};

export const getSystemRoles = (): RoleWithScopes[] =>
    ROLE_HIERARCHY.map((role) => ({
        roleUuid: role,
        name: ProjectMemberRoleLabels[role],
        description: ProjectMemberRoleLabels[role],
        ownerType: 'system',
        scopes: getAllScopesForRole(role),
        organizationUuid: null,
        createdAt: null,
        updatedAt: null,
        createdBy: null,
    }));

export const isSystemRole = (roleUuid: string): roleUuid is ProjectMemberRole =>
    ROLE_HIERARCHY.includes(roleUuid as (typeof ROLE_HIERARCHY)[number]);
